import React, { useState } from 'react';
import {
    Music, Sparkles, BookOpen, Film, Flame, ExternalLink,
    Youtube, Apple, Cloud, Share2, Loader2, Languages,
} from 'lucide-react';
import { Button } from './ui/button';

// ─── SongDeepDive ─────────────────────────────────────────────────
// Renders the Claude-generated four-section editorial (meaning,
// background, appearances, hot take) for a music-platform bookmark.
// The deep-dive is generated at ingest time and stored on the bookmark
// doc as `song_deep_dive`. This component is presentational — no
// fetching of its own.
//
// Bilingual: when `song_deep_dive.language` != 'en', each section has
// both an `en` and `local` field. A single language toggle at the top
// flips all four sections simultaneously.
//
// Share card: the button opens the OG-image endpoint in a new tab so
// the user can right-click → Save As on desktop, or long-press → save
// image on mobile. Works everywhere without needing native share APIs.

// Map platform key → { label, icon, brand color }. Used to render the
// "Listen on…" button row.
const PLATFORM_META = {
    spotify:       { label: 'Spotify',       icon: Music,  color: '#1DB954' },
    apple_music:   { label: 'Apple Music',   icon: Apple,  color: '#FA243C' },
    youtube_music: { label: 'YouTube Music', icon: Youtube, color: '#FF0033' },
    soundcloud:    { label: 'SoundCloud',    icon: Cloud,  color: '#FF5500' },
    bandcamp:      { label: 'Bandcamp',      icon: Music,  color: '#629AA9' },
    tidal:         { label: 'Tidal',         icon: Music,  color: '#000000' },
    deezer:        { label: 'Deezer',        icon: Music,  color: '#FF0092' },
    amazon_music:  { label: 'Amazon Music',  icon: Music,  color: '#25D1DA' },
    pandora:       { label: 'Pandora',       icon: Music,  color: '#005483' },
    anghami:       { label: 'Anghami',       icon: Music,  color: '#8324c5' },
};

export function SongDeepDive({ bookmark, apiBase = '/api' }) {
    const meta = bookmark.song_meta || {};
    const dd = bookmark.song_deep_dive;
    const hasLocal = dd?.language && dd.language !== 'en';
    // When bilingual, default to the user's own language.
    const [showLocal, setShowLocal] = useState(hasLocal);
    const [sharing, setSharing] = useState(false);

    // Pick the right side of the bilingual pair; fall back to `en` if
    // the local translation ended up empty for that section.
    const pick = (section) => {
        if (!section) return '';
        if (showLocal && section.local) return section.local;
        return section.en || section.local || '';
    };

    const platformKey = meta.platform || 'spotify';
    const platformIcon = PLATFORM_META[platformKey]?.icon || Music;
    const platformColor = PLATFORM_META[platformKey]?.color || '#10b981';

    const listenLinks = meta.listen_links || {};

    const onShare = async () => {
        setSharing(true);
        const shareUrl = `${apiBase}/og/song/${bookmark.bookmark_id}.png`;
        try {
            // Prefer the native share sheet on mobile — feels magical.
            if (navigator.share && navigator.canShare) {
                try {
                    const resp = await fetch(shareUrl);
                    const blob = await resp.blob();
                    const file = new File([blob], `${meta.title || 'song'}.png`, { type: 'image/png' });
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: `${meta.title}${meta.artist ? ` — ${meta.artist}` : ''}`,
                            text: dd?.hot_take?.en || dd?.meaning?.en?.slice(0, 140) || '',
                        });
                        setSharing(false);
                        return;
                    }
                } catch {
                    /* fall through to open-in-new-tab */
                }
            }
            window.open(shareUrl, '_blank', 'noopener');
        } finally {
            setSharing(false);
        }
    };

    // Sections in a stable render order.
    const sections = [
        { key: 'meaning',     icon: Sparkles, title: 'What it means',       body: pick(dd?.meaning),     tone: 'emerald' },
        { key: 'background',  icon: BookOpen, title: 'How it came to be',   body: pick(dd?.background),  tone: 'sky' },
        { key: 'appearances', icon: Film,     title: 'Where you\u2019ve heard it', body: pick(dd?.appearances), tone: 'purple' },
        { key: 'hot_take',    icon: Flame,    title: 'Hot take',            body: pick(dd?.hot_take),    tone: 'amber' },
    ].filter((s) => s.body);

    if (!dd || sections.length === 0) {
        // Deep-dive hasn't generated yet OR every section came back empty.
        return (
            <div className="bg-white border border-border/60 rounded-2xl p-6 text-center" data-testid="song-deep-dive-empty">
                <Music className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                    {bookmark.status === 'completed'
                        ? 'Deep-dive not available for this track yet. Try Reprocess to regenerate.'
                        : 'Preparing the song deep-dive… (usually 20-40 seconds)'}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="song-deep-dive">
            {/* Cover art hero */}
            <div className="bg-white border border-border/60 rounded-2xl overflow-hidden">
                <div className="relative">
                    {meta.cover_art_url ? (
                        <>
                            <div
                                className="absolute inset-0 blur-3xl opacity-40"
                                style={{ backgroundImage: `url(${meta.cover_art_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                                aria-hidden="true"
                            />
                            <div className="relative flex items-center gap-4 p-5 bg-gradient-to-br from-white/0 to-white/40">
                                <img
                                    src={meta.cover_art_url}
                                    alt=""
                                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover shadow-lg ring-1 ring-black/5 flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: platformColor }}>
                                        {React.createElement(platformIcon, { className: 'w-3 h-3' })}
                                        <span>{PLATFORM_META[platformKey]?.label || 'Song'}</span>
                                    </div>
                                    <h3 className="font-heading font-bold text-lg sm:text-xl leading-tight truncate" data-testid="song-title">
                                        {meta.title || bookmark.title}
                                    </h3>
                                    {meta.artist && (
                                        <p className="text-sm text-muted-foreground truncate" data-testid="song-artist">
                                            {meta.artist}
                                            {meta.album && <> · <span className="italic">{meta.album}</span></>}
                                            {meta.year && <> · {meta.year}</>}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="p-5">
                            <h3 className="font-heading font-bold text-lg">{meta.title || bookmark.title}</h3>
                            {meta.artist && <p className="text-sm text-muted-foreground">{meta.artist}</p>}
                        </div>
                    )}
                </div>

                {/* Listen anywhere row */}
                <div className="px-4 py-3 border-t border-border/60 flex flex-wrap gap-2 items-center">
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mr-1">Listen on</span>
                    {['spotify', 'apple_music', 'youtube_music'].map((k) => {
                        const link = listenLinks[k];
                        if (!link) return null;
                        const p = PLATFORM_META[k];
                        const Icon = p.icon;
                        return (
                            <a
                                key={k}
                                href={link}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border border-border/60 hover:border-border hover:bg-secondary/60 transition-colors"
                                data-testid={`listen-on-${k}`}
                            >
                                <Icon className="w-3.5 h-3.5" style={{ color: p.color }} />
                                {p.label}
                            </a>
                        );
                    })}
                    <div className="ml-auto flex items-center gap-2">
                        {hasLocal && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowLocal((v) => !v)}
                                className="h-8 px-2 text-xs gap-1.5"
                                data-testid="song-language-toggle"
                                title="Toggle between your language and English"
                            >
                                <Languages className="w-3.5 h-3.5" />
                                {showLocal ? 'EN' : (dd.language || 'local').toUpperCase()}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onShare}
                            disabled={sharing}
                            className="h-8 gap-1.5 rounded-full"
                            data-testid="song-share-card-btn"
                        >
                            {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                            <span className="hidden sm:inline">Share card</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Four editorial sections */}
            {sections.map((s) => {
                const tones = {
                    emerald: 'bg-emerald-50/70 text-emerald-900 border-emerald-100',
                    sky:     'bg-sky-50/70 text-sky-900 border-sky-100',
                    purple:  'bg-purple-50/70 text-purple-900 border-purple-100',
                    amber:   'bg-amber-50/70 text-amber-900 border-amber-100',
                };
                const iconTones = {
                    emerald: 'text-emerald-600',
                    sky:     'text-sky-600',
                    purple:  'text-purple-600',
                    amber:   'text-amber-600',
                };
                const Icon = s.icon;
                // Hebrew / Arabic / Farsi need dir=rtl so punctuation and
                // wrapping don't flip. Cheap detection: majority chars.
                const rtl = showLocal && /[\u0590-\u05FF\u0600-\u06FF]/.test(s.body);
                return (
                    <section
                        key={s.key}
                        className={`rounded-2xl border p-5 ${tones[s.tone]}`}
                        data-testid={`song-section-${s.key}`}
                        dir={rtl ? 'rtl' : 'ltr'}
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 ${iconTones[s.tone]}`} />
                            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider">{s.title}</h4>
                        </div>
                        <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{s.body}</p>
                    </section>
                );
            })}

            {/* Sources */}
            {dd.sources && dd.sources.length > 0 && (
                <div className="rounded-2xl border border-border/60 bg-white p-4">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Sources</p>
                    <ul className="space-y-1.5">
                        {dd.sources.slice(0, 6).map((src, i) => (
                            <li key={i} className="text-xs text-muted-foreground truncate">
                                <a
                                    href={src.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-1 hover:text-primary hover:underline"
                                >
                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{src.title || src.url}</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
